import React, { useState } from 'react';
import CommonModal from '../../../extras/commonModal';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import {
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Box,
  Checkbox,
  ListItemText,
  Chip,
  FormHelperText,
  CircularProgress,
} from '@material-ui/core';
import './studentGroupModal.css';
import { createStudentGroups } from '../../../services/apiService';
import { toast } from 'react-toastify';


const initialFormState = {
  groupName: '',
  groupDescription: '',
  groupType: '',
  staticOption: '', // 'userIds' or 'emails'
  userIdList: '',
  emailList: '',
  dynamicOption: '', // 'program', 'cohort', 'almacohort', 'daterange'
  // Program multi-selects
  programCourseList: [],
  programPlanList: [],
  programSubplanList: [],
  // Cohort multi-selects
  selectedCohorts: [],
  dateRange: '', // Combined date range display
  startDate: null, // For DatePicker
  endDate: null, // For DatePicker
};

// Options will be fetched dynamically

const StudentGroupModal = ({
  openCreateModal,
  setOpenCreateModal,
  cohortListProp = [],
  almaxCohortListProp = [],
  programOptionsProp = { course: [], plan: [], subplan: [] },
       onRefresh
}) => {
  const [formState, setFormState] = useState(initialFormState);
  const [isLoading, setIsLoading] = useState(false);
  const [cohortList, setCohortList] = useState([]);
  const [almaxCohortList, setAlmaxCohortList] = useState([]);
  const [programOptions, setProgramOptions] = useState({
    course: [],
    plan: [],
    subplan: [],
  });

  // Email validation regex - more comprehensive
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  // User ID validation regex (alphanumeric, dots, underscores, hyphens)
  const userIdRegex = /^[a-zA-Z0-9._-]+$/;

  // Function to process user input for comma-separated or newline-separated lists
  const processUserInput = (input) => {
    return input
      .replace(/\n/g, ',') // Convert newlines to commas
      .split(',')
      .map((item) => item.trim())
      .filter((item) => item.length > 0);
  };

  // Function to count and validate user IDs
  const getUserIdValidation = (input) => {
    if (!input.trim()) return { count: 0, validCount: 0, isValid: true };

    const items = processUserInput(input);
    const validItems = items.filter((item) => userIdRegex.test(item));

    return {
      count: items.length,
      validCount: validItems.length,
      isValid: validItems.length === items.length && items.length > 0,
    };
  };

  // Function to count and validate emails
  const getEmailValidation = (input) => {
    if (!input.trim()) return { count: 0, validCount: 0, isValid: true };

    const items = processUserInput(input);
    const validItems = items.filter((item) => emailRegex.test(item));

    return {
      count: items.length,
      validCount: validItems.length,
      isValid: validItems.length === items.length && items.length > 0,
    };
  };

  // Format date range for display
  // console.log(isGlobalLoading, 'asdkj');
  const formatDateRange = (startDate, endDate) => {
    if (!startDate || !endDate) return '';

    const formatDate = (date) => {
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    };

    return `${formatDate(startDate)} - ${formatDate(endDate)}`;
  };

  // Receive pre-fetched global data via props
  React.useEffect(() => {
    if (!openCreateModal) return;
    // Assign props to local state holders for easy usage
    setCohortList(Array.isArray(cohortListProp) ? cohortListProp : []);
    setAlmaxCohortList(
      Array.isArray(almaxCohortListProp) ? almaxCohortListProp : []
    );
    setProgramOptions(
      programOptionsProp && typeof programOptionsProp === 'object'
        ? programOptionsProp
        : { course: [], plan: [], subplan: [] }
    );
  }, [
    openCreateModal,
    cohortListProp,
    almaxCohortListProp,
    programOptionsProp,
  ]);

  // Handle date range changes
  const handleDateRangeChange = (dates) => {
    const [start, end] = dates;
    setFormState((prev) => ({
      ...prev,
      startDate: start,
      endDate: end,
      dateRange: start && end ? formatDateRange(start, end) : '',
    }));
  };

  const handleClose = () => {
    onRefresh();
    setOpenCreateModal(false);
    setFormState(initialFormState); // Reset form on close
    setIsLoading(false); // Reset loading state
  };

  const handleInputChange = (field, value) => {
    setFormState((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleGroupTypeChange = (value) => {
    // Reset relevant fields when group type changes
    setFormState((prev) => ({
      ...prev,
      groupType: value,
      staticOption: '',
      userIdList: '',
      emailList: '',
      dynamicOption: '',
      // program selections
      programCourseList: [],
      programPlanList: [],
      programSubplanList: [],
      // cohort selections
      selectedCohorts: [],
      dateRange: '',
      startDate: null,
      endDate: null,
    }));
  };

  const handleStaticOptionChange = (value) => {
    // Reset input fields when static option changes
    setFormState((prev) => ({
      ...prev,
      staticOption: value,
      userIdList: '',
      emailList: '',
    }));
  };

  const handleDynamicOptionChange = (value) => {
    // Reset sub-fields when dynamic option changes
    setFormState((prev) => ({
      ...prev,
      dynamicOption: value,
      // clear program-related selections
      programCourseList: [],
      programPlanList: [],
      programSubplanList: [],
      // clear cohort selections
      selectedCohorts: [],
      // clear date range
      dateRange: '',
      startDate: null,
      endDate: null,
    }));
  };

  const validateForm = () => {
    if (!formState.groupName.trim()) return false;
    if (!formState.groupType) return false;

    if (formState.groupType === 'static') {
      if (!formState.staticOption) return false;
      if (formState.staticOption === 'userIds') {
        const validation = getUserIdValidation(formState.userIdList);
        return validation.isValid && validation.validCount > 0;
      }
      if (formState.staticOption === 'emails') {
        const validation = getEmailValidation(formState.emailList);
        return validation.isValid && validation.validCount > 0;
      }
      return false;
    }

    if (formState.groupType === 'dynamic') {
      if (!formState.dynamicOption) return false;

      switch (formState.dynamicOption) {
        case 'program':
          return (
            (Array.isArray(formState.programCourseList) &&
              formState.programCourseList.length > 0) ||
            (Array.isArray(formState.programPlanList) &&
              formState.programPlanList.length > 0) ||
            (Array.isArray(formState.programSubplanList) &&
              formState.programSubplanList.length > 0)
          );
        case 'cohort':
        case 'almacohort':
          return (
            Array.isArray(formState.selectedCohorts) &&
            formState.selectedCohorts.length > 0
          );
        case 'daterange':
          return !!(formState.startDate && formState.endDate);
        default:
          return false;
      }
    }

    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      // Transform data for API submission
      const submitData = {
        groupName: formState.groupName,
        ...(formState.groupDescription && {
          groupDecr: formState.groupDescription,
        }),
        groupType: formState.groupType,
        ...(formState.groupType === 'static' && {
          params: {
            type:
              formState.staticOption === 'userIds'
                ? 'userId'
                : formState.staticOption === 'emails'
                ? 'emailId'
                : formState.staticOption,
            ...(formState.staticOption === 'userIds' && {
              userIds: processUserInput(formState.userIdList),
            }),
            ...(formState.staticOption === 'emails' && {
              emailIds: processUserInput(formState.emailList),
            }),
          },
        }),
        ...(formState.groupType === 'dynamic' && {
          params: (() => {
            const opt = formState.dynamicOption;
            if (opt === 'program') {
              return {
                type: 'program',
                program: {
                  course: formState.programCourseList,
                  subplan: formState.programSubplanList,
                  plan: formState.programPlanList,
                },
              };
            }
            if (opt === 'cohort') {
              return { type: 'cohort', cohort: formState.selectedCohorts };
            }
            if (opt === 'almacohort') {
              return {
                type: 'almaxCohort',
                almaxCohort: formState.selectedCohorts,
              };
            }
            if (opt === 'daterange') {
              return {
                type: 'enrolmentDate',
                enrolmentDate: {
                  startDate: formState.startDate,
                  endDate: formState.endDate,
                },
              };
            }
            return {};
          })(),
        }),
      };

      const response = await createStudentGroups(submitData);

      if (response && response.success !== false) {
        toast.success(response.message || 'Student group created successfully');
        handleClose();
        
        if (setOpenCreateModal && typeof setOpenCreateModal === 'function') {
          setOpenCreateModal(false);
        }
      } else {
        const errorMessage =
          response?.message ||
          response?.error ||
          'Failed to create student group';
        toast.error(errorMessage);
      }
    } catch (error) {
      console.error('Error creating student group:', error);
      const errorMessage =
        'An unexpected error occurred while creating the student group';

      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // Get validation info for user IDs
  const userIdValidation = getUserIdValidation(formState.userIdList);
  const emailValidation = getEmailValidation(formState.emailList);

  return (
    <CommonModal
      isOpen={openCreateModal}
      setIsOpen={handleClose}
      removePadding
      classStyle='w-[680px] max-w-[90vw] rounded-2xl overflow-y-auto'
    >
      <div
        className='flex flex-col'
        style={{
          padding: '28px 32px',
          maxHeight: '85vh',
          backgroundColor: 'var(--card-background, #fff)',
        }}
      >
        <h2
          style={{
            fontSize: 18,
            fontWeight: 700,
            color: 'var(--heading-primary, #111827)',
            marginBottom: 20,
          }}
        >
          Create New Group
        </h2>

        {/* Form content */}
        <div style={{ flex: 1, overflowY: 'auto' }}>
          <Grid container spacing={3}>
            {/* Row 1: Group Name and Group Type - Always Visible */}
            <Grid item xs={12}>
              <Grid container spacing={3}>
                <Grid item xs={6}>
                  <TextField
                    label='Group Name'
                    name='groupName'
                    variant='outlined'
                    fullWidth
                    type='text'
                    value={formState.groupName}
                    onChange={(e) =>
                      handleInputChange('groupName', e.target.value)
                    }
                    placeholder='Enter group name'
                  />
                </Grid>
                <Grid item xs={6}>
                  <FormControl variant='outlined' fullWidth>
                    <InputLabel>Group Type</InputLabel>
                    <Select
                      value={formState.groupType}
                      onChange={(e) => handleGroupTypeChange(e.target.value)}
                      label='Group Type'
                    >
                      <MenuItem value='static'>Static</MenuItem>
                      <MenuItem value='dynamic'>Dynamic</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Grid>

            {/* Row 1.5: Group Description - Full Width */}
            <Grid item xs={12}>
              <TextField
                label='Group Description'
                name='groupDescription'
                variant='outlined'
                fullWidth
                multiline
                rows={2}
                value={formState.groupDescription}
                onChange={(e) =>
                  handleInputChange('groupDescription', e.target.value)
                }
                placeholder='Enter group description (optional)'
                helperText='Provide a brief description of the group'
              />
            </Grid>
            {formState.groupType === 'static' && (
              <Grid item xs={12}>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <FormControl variant='outlined' fullWidth>
                      <InputLabel>Choose Input Type</InputLabel>
                      <Select
                        value={formState.staticOption}
                        onChange={(e) =>
                          handleStaticOptionChange(e.target.value)
                        }
                        label='Choose Input Type'
                      >
                        <MenuItem value='userIds'>User ID List</MenuItem>
                        <MenuItem value='emails'>Email List</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    {formState.staticOption === 'userIds' && (
                      <TextField
                        label='User ID List (comma separated or new line separated)'
                        name='userIdList'
                        variant='outlined'
                        fullWidth
                        multiline
                        rows={3}
                        value={formState.userIdList}
                        onChange={(e) =>
                          handleInputChange('userIdList', e.target.value)
                        }
                        placeholder='user1, user2, user3 or enter each on new line'
                        helperText={
                          formState.userIdList.trim()
                            ? `${userIdValidation.validCount} valid User IDs out of ${userIdValidation.count} entered`
                            : 'Enter user IDs separated by commas or new lines'
                        }
                        error={
                          formState.userIdList.trim().length > 0 &&
                          !userIdValidation.isValid
                        }
                      />
                    )}
                    {formState.staticOption === 'emails' && (
                      <TextField
                        label='Email List (comma separated or new line separated)'
                        name='emailList'
                        variant='outlined'
                        fullWidth
                        multiline
                        rows={3}
                        value={formState.emailList}
                        onChange={(e) =>
                          handleInputChange('emailList', e.target.value)
                        }
                        placeholder='email1@example.com, email2@example.com or enter each on new line'
                        helperText={
                          formState.emailList.trim()
                            ? `${emailValidation.validCount} valid emails out of ${emailValidation.count} entered`
                            : 'Enter emails separated by commas or new lines'
                        }
                        error={
                          formState.emailList.trim().length > 0 &&
                          !emailValidation.isValid
                        }
                      />
                    )}
                  </Grid>
                </Grid>
              </Grid>
            )}

            {/* Row 3: Dynamic Group Options - Only show when dynamic is selected */}
            {formState.groupType === 'dynamic' && (
              <Grid item xs={12}>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <FormControl variant='outlined' fullWidth>
                      <InputLabel>Dynamic Option</InputLabel>
                      <Select
                        value={formState.dynamicOption}
                        onChange={(e) =>
                          handleDynamicOptionChange(e.target.value)
                        }
                        label='Dynamic Option'
                      >
                        <MenuItem value='program'>Program</MenuItem>
                        <MenuItem value='cohort'>Cohort</MenuItem>
                        <MenuItem value='almacohort'>AlmaX Cohort</MenuItem>
                        <MenuItem value='daterange'>Date Range</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    {/* Program Type Selection */}
                    {formState.dynamicOption === 'program' && (
                      <Grid container spacing={3}>
                        <Grid item xs={12}>
                          <FormControl variant='outlined' fullWidth>
                            <InputLabel>Course</InputLabel>
                            <Select
                              multiple
                              value={formState.programCourseList}
                              onChange={(e) =>
                                handleInputChange(
                                  'programCourseList',
                                  e.target.value
                                )
                              }
                              label='Course'
                              renderValue={(selected) => (
                                <Box
                                  sx={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: 0.5,
                                  }}
                                >
                                  {selected.map((value) => (
                                    <Chip
                                      key={value}
                                      label={
                                        value === 'data-science'
                                          ? 'data-science'
                                          : 'web3'
                                      }
                                      size='small'
                                    />
                                  ))}
                                </Box>
                              )}
                              sx={{
                                maxHeight: 280,
                              }}
                            >
                              {programOptions.course.map((val) => (
                                <MenuItem key={val} value={val}>
                                  <Checkbox
                                    checked={
                                      formState.programCourseList.indexOf(val) >
                                      -1
                                    }
                                  />
                                  <ListItemText primary={val} />
                                </MenuItem>
                              ))}
                            </Select>
                            <FormHelperText>
                              Select one or more courses
                            </FormHelperText>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                          <FormControl variant='outlined' fullWidth>
                            <InputLabel>Plan</InputLabel>
                            <Select
                              multiple
                              value={formState.programPlanList}
                              onChange={(e) =>
                                handleInputChange(
                                  'programPlanList',
                                  e.target.value
                                )
                              }
                              label='Plan'
                              renderValue={(selected) => (
                                <Box
                                  sx={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: 0.5,
                                  }}
                                >
                                  {selected.map((value) => (
                                    <Chip
                                      key={value}
                                      label={
                                        value.charAt(0).toUpperCase() +
                                        value.slice(1)
                                      }
                                      size='small'
                                    />
                                  ))}
                                </Box>
                              )}
                              sx={{
                                maxHeight: 280,
                              }}
                            >
                              {programOptions.plan.map((val) => (
                                <MenuItem key={val} value={val}>
                                  <Checkbox
                                    checked={
                                      formState.programPlanList.indexOf(val) >
                                      -1
                                    }
                                  />
                                  <ListItemText primary={val} />
                                </MenuItem>
                              ))}
                            </Select>
                            <FormHelperText>Select plan(s)</FormHelperText>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                          <FormControl variant='outlined' fullWidth>
                            <InputLabel>Subplan</InputLabel>
                            <Select
                              multiple
                              value={formState.programSubplanList}
                              onChange={(e) =>
                                handleInputChange(
                                  'programSubplanList',
                                  e.target.value
                                )
                              }
                              label='Subplan'
                              renderValue={(selected) => (
                                <Box
                                  sx={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: 0.5,
                                  }}
                                >
                                  {selected.map((value) => (
                                    <Chip
                                      key={value}
                                      label={
                                        value.charAt(0).toUpperCase() +
                                        value.slice(1)
                                      }
                                      size='small'
                                    />
                                  ))}
                                </Box>
                              )}
                              sx={{
                                maxHeight: 280,
                              }}
                            >
                              {programOptions.subplan.map((val) => (
                                <MenuItem key={val} value={val}>
                                  <Checkbox
                                    checked={
                                      formState.programSubplanList.indexOf(
                                        val
                                      ) > -1
                                    }
                                  />
                                  <ListItemText primary={val} />
                                </MenuItem>
                              ))}
                            </Select>
                            <FormHelperText>Select subplan(s)</FormHelperText>
                          </FormControl>
                        </Grid>
                      </Grid>
                    )}

                    {/* Cohort Selection */}
                    {(formState.dynamicOption === 'cohort' ||
                      formState.dynamicOption === 'almacohort') && (
                      <FormControl variant='outlined' fullWidth>
                        <InputLabel>
                          {formState.dynamicOption === 'cohort'
                            ? 'Select Cohort'
                            : 'Select AlmaX Cohort'}
                        </InputLabel>
                        <Select
                          multiple
                          value={formState.selectedCohorts}
                          onChange={(e) =>
                            handleInputChange('selectedCohorts', e.target.value)
                          }
                          label={
                            formState.dynamicOption === 'cohort'
                              ? 'Select Cohort'
                              : 'Select AlmaX Cohort'
                          }
                          renderValue={(selected) => (
                            <Box
                              sx={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: 0.5,
                              }}
                            >
                              {selected.map((id) => {
                                const list =
                                  formState.dynamicOption === 'cohort'
                                    ? cohortList
                                    : almaxCohortList;
                                const co = list.find((c) => c.id === id);
                                return (
                                  <Chip
                                    key={id}
                                    label={co?.name || id}
                                    size='small'
                                  />
                                );
                              })}
                            </Box>
                          )}
                          MenuProps={{
                            PaperProps: { style: { maxHeight: 240 } },
                            getContentAnchorEl: null,
                            anchorOrigin: {
                              vertical: 'bottom',
                              horizontal: 'left',
                            },
                            transformOrigin: {
                              vertical: 'top',
                              horizontal: 'left',
                            },
                          }}
                        >
                          {(formState.dynamicOption === 'cohort'
                            ? cohortList
                            : almaxCohortList
                          ).map((cohort) => (
                            <MenuItem key={cohort.id} value={cohort.id} dense>
                              <Checkbox
                                size='small'
                                checked={
                                  formState.selectedCohorts.indexOf(cohort.id) >
                                  -1
                                }
                              />
                              <ListItemText
                                primaryTypographyProps={{ variant: 'body2' }}
                                primary={cohort.name}
                              />
                            </MenuItem>
                          ))}
                        </Select>
                        <FormHelperText>
                          You can pick multiple cohorts
                        </FormHelperText>
                      </FormControl>
                    )}

                    {formState.dynamicOption === 'daterange' && (
                      <Box sx={{ width: '100%', position: 'relative' }}>
                        <FormHelperText sx={{ mb: 1 }}>
                          Select enrolment date range
                        </FormHelperText>
                        <DatePicker
                          selectsRange={true}
                          startDate={formState.startDate}
                          endDate={formState.endDate}
                          onChange={handleDateRangeChange}
                          placeholderText='Select date range'
                          isClearable={true}
                          showPopperArrow={false}
                          popperClassName='date-picker-popper'
                          dateFormat='MMM dd, yyyy'
                          popperProps={{
                            positionFixed: true,
                            strategy: 'fixed',
                          }}
                          className='date-range-input'
                          style={{ width: '100%' }}
                        />
                      </Box>
                    )}
                  </Grid>
                </Grid>
              </Grid>
            )}
          </Grid>
        </div>

        {/* Action buttons */}
        <div
          style={{
            marginTop: 24,
            paddingTop: 20,
            borderTop: '1px solid var(--border-light, rgba(0,0,0,0.07))',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 12,
          }}
        >
          <button
            onClick={handleClose}
            style={{
              padding: '10px 28px',
              borderRadius: 8,
              border: '1px solid var(--border-light, rgba(0,0,0,0.12))',
              backgroundColor: 'transparent',
              color: 'var(--text-primary, #111827)',
              fontSize: 14,
              fontWeight: 500,
              cursor: 'pointer',
              transition: 'background-color 150ms',
            }}
            onMouseEnter={(e) => (e.target.style.backgroundColor = 'var(--hover-bg, #F3F4F6)')}
            onMouseLeave={(e) => (e.target.style.backgroundColor = 'transparent')}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={isLoading || !validateForm()}
            style={{
              padding: '10px 28px',
              borderRadius: 8,
              border: 'none',
              backgroundColor: validateForm() && !isLoading ? 'var(--primary-main, #4F46E5)' : 'var(--border-light, #D1D5DB)',
              color: validateForm() && !isLoading ? '#fff' : 'var(--text-secondary, #9CA3AF)',
              fontSize: 14,
              fontWeight: 500,
              cursor: validateForm() && !isLoading ? 'pointer' : 'not-allowed',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              transition: 'background-color 150ms, box-shadow 150ms',
              boxShadow: validateForm() && !isLoading ? '0 1px 4px rgba(79,70,229,0.3)' : 'none',
            }}
          >
            {isLoading ? (
              <>
                <CircularProgress size={16} style={{ color: 'white' }} />
                Creating...
              </>
            ) : (
              'Create Group'
            )}
          </button>
        </div>
      </div>
    </CommonModal>
  );
};

export default StudentGroupModal;
